import {
  BarController,
  BarElement, CategoryScale, Chart as ChartJS, Legend, type LegendItem,
  LinearScale, LineController, LineElement, PointElement, Tooltip,
} from 'chart.js';
import { useMemo } from 'react';
import { Chart } from 'react-chartjs-2';
import type { DeepWritable } from 'ts-essentials';
import {
  hexAndOpacityToRGBA, rgbColorToString,
} from '~/_shared/components/colorPicker/colorPicker.helpers';
import { OverlayScrollbarComponent } from '~/_shared/components/scrollbar/overlayScrollbar.component';
import { ScrollbarType } from '~/_shared/components/scrollbar/scrollbar.component';
import { useTheme } from '~/_shared/themes/theme.hooks';
import { cloneDeep } from '~/_shared/utils/object/deepMerge';
import type { ChartItem } from './chartItem.types';

const WIDTH_PER_LABEL = 100; // Width of each label in pixels
const LARGE_DATASET_LABEL_COUNT = 80; // Above this many labels the chart switches to a compact, performance-optimized rendering mode
const HUGE_DATASET_LABEL_COUNT = 800; // Above this many labels the chart switches to an even more compact, performance-optimized rendering mode
const LARGE_BAR_THICKNESS = 3; // Width (px) of each individual bar, once large
const LARGE_GROUP_GAP = 2; // Gap (px) between each label's group of bars, once large
const HUGE_BAR_THICKNESS = 2; // Width (px) of each individual bar, once huge
const HUGE_GROUP_GAP = 1; // Gap (px) between each label's group of bars, once huge
const TRUNCATED_LABEL_LENGTH = 30; // Maximum length of each label before truncation

const GRAPH_PRESET_COLORS = [
  '#E63946', '#9B5DE5', '#457B9D', '#6C757D', '#2A9D8F', '#FFB703',
] as const;

const getAlternatingColor = (index: number, opacity = 0.75) => {
  const color = GRAPH_PRESET_COLORS[index % GRAPH_PRESET_COLORS.length];
  const c = hexAndOpacityToRGBA((color || GRAPH_PRESET_COLORS[0]), opacity);
  return rgbColorToString(c);
};

const truncateLabel = (label: string) => (
  label.length > TRUNCATED_LABEL_LENGTH ? `${label.slice(0, TRUNCATED_LABEL_LENGTH - 3)}...` : label
);

const normalizeData = (data: number[]) => {
  let min = Math.min(...data);
  if (min > 0) {
    min = 0;
  }
  const max = Math.max(...data);
  const range = max - min;

  // Avoid division by zero if all values are the same
  if (range === 0) {
    return data.map(() => 1);
  }

  return data.map(val => (((val - min) / range) * 100).toFixed(2));
};

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Legend, Tooltip, LineController, BarController);

export const ChartItemVisualizer = ({
  shouldNormalize,
  metrics,
}: ChartItem) => {

  const theme = useTheme();

  // truncate each label/name to TRUNCATED_LABEL_LENGTH characters so they fit
  const metricsWithAdjustedNames = useMemo(() => ({
    ...metrics,
    labels: metrics.labels.map(truncateLabel),
    barValues: metrics.barValues.map(metric => ({ ...metric, name: truncateLabel(metric.name) })),
    medians: metrics.medians.map(metric => ({ ...metric, name: truncateLabel(metric.name) })),
  }), [metrics]);

  const mutableMetrics = useMemo(() => (
    // clone because the Chart.js library requires mutable objects
    cloneDeep(metricsWithAdjustedNames) as DeepWritable<typeof metrics>
  ), [metricsWithAdjustedNames]);

  const numberOfLabels = mutableMetrics.labels.length;
  const numberOfBarSeries = mutableMetrics.barValues.length;
  // Above these thresholds, rendering and hovering over a full-width chart gets noticeably slow,
  // so we switch to an increasingly compact layout and cheaper chart.js options.
  const isLargeDataset = numberOfLabels > LARGE_DATASET_LABEL_COUNT;
  const isHugeDataset = numberOfLabels > HUGE_DATASET_LABEL_COUNT;
  const compactBarThickness = isHugeDataset ? HUGE_BAR_THICKNESS : LARGE_BAR_THICKNESS;
  const compactGroupGap = isHugeDataset ? HUGE_GROUP_GAP : LARGE_GROUP_GAP;
  const compactBarGroupWidth = numberOfBarSeries * compactBarThickness;
  const widthPerLabel = isLargeDataset ? compactBarGroupWidth + compactGroupGap : WIDTH_PER_LABEL;

  const data = useMemo(() => {
    const minAndMaxValuesPerMetric = mutableMetrics.barValues.map(metric => {
      return {
        min: Math.min(...metric.values),
        max: Math.max(...metric.values),
      };
    });

    return {
      labels: mutableMetrics.labels,
      datasets: [
        ...mutableMetrics.barValues.map((metric, index) => ({
          type: 'bar' as const,
          label: metric.name,
          rawData: metric.values,
          data: shouldNormalize
            ? normalizeData(metric.values)
            : metric.values,
          backgroundColor: getAlternatingColor(index),
          order: index + mutableMetrics.barValues.length,
          // Data is already aligned to the (sorted, consistent) labels array, so chart.js can skip re-sorting it.
          normalized: true,
          ...(isLargeDataset ? {
            // A fixed barThickness (rather than barPercentage/categoryPercentage ratios) makes chart.js place
            // bars flush against each other within a group, and decouples the bar width from the gap between groups.
            barThickness: compactBarThickness,
          } : {}),
        })),
        ...mutableMetrics.medians.map((metric, index) => {
          const value = shouldNormalize
            ? normalizeData([
              metric.value,
              minAndMaxValuesPerMetric[index]?.max || 0,
              minAndMaxValuesPerMetric[index]?.min || 0,
            ])[0] || 0
            : metric.value;
          const rawData = mutableMetrics.labels.map(() => metric.value);
          const lineData = mutableMetrics.labels.map(() => value);
          return {
            type: 'line' as const,
            label: metric.name,
            rawData,
            data: lineData,
            borderColor: getAlternatingColor(index, 1),
            backgroundColor: getAlternatingColor(index, 1),
            fill: false,
            tension: 0.4,
            pointRadius: 0, // Hide points in line chart
            order: index, // Ensure lines are drawn above bars
            normalized: true,
          };
        }),
      ],
    };
  }, [mutableMetrics, shouldNormalize, isLargeDataset, compactBarThickness]);

  const options = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    ...(isLargeDataset ? {
      // Skip the per-element grow-in animation, which is costly to compute across hundreds of elements.
      animation: false as const,
      // 'index' looks up the hovered element via the scale directly instead of measuring the distance to
      // every element on every mousemove (chart.js default 'nearest' mode), which is what makes hover laggy.
      interaction: {
        mode: 'index' as const,
        intersect: false,
      },
    } : {}),
    ...(isHugeDataset ? {
      // Cap the canvas pixel buffer so very wide charts don't allocate huge (width * devicePixelRatio) canvases.
      devicePixelRatio: 1,
    } : {}),
    plugins: {
      legend: {
        labels: {
          boxHeight: 0,
          sort: (a: LegendItem, b: LegendItem) => {
            const totalNumberOfBarValues = mutableMetrics.barValues.length;
            const aDatasetIndex = a.datasetIndex || 0;
            const bDatasetIndex = b.datasetIndex || 0;
            return (
              (aDatasetIndex >= totalNumberOfBarValues
                ? totalNumberOfBarValues * 2 + aDatasetIndex
                : aDatasetIndex)
              -
              (bDatasetIndex >= totalNumberOfBarValues
                ? totalNumberOfBarValues * 2 + bDatasetIndex
                : bDatasetIndex)
            );
          },
          generateLabels: (chart: ChartJS) => {
            const originalLabels = ChartJS.defaults.plugins.legend.labels.generateLabels(chart);

            return originalLabels.map((label) => {
              return {
                ...label,
                color: theme.textColors.primary,
                ...((label.datasetIndex || 0) >= mutableMetrics.barValues.length ? {
                  lineWidth: 5,
                } : {
                  lineWidth: 12,
                  strokeStyle: label.fillStyle,
                }),
              };
            });
          },
        },
        position: 'top' as const,
      },
      tooltip: {
        ...(isLargeDataset ? {
          // Median lines are identical across every label, so surfacing them in the per-index
          // tooltip just repeats the same value for every hovered bar. Hide them from the tooltip
          // while still rendering the line itself on the chart.
          filter: (tooltipItem: { datasetIndex: number }) => (
            tooltipItem.datasetIndex < mutableMetrics.barValues.length
          ),
        } : {}),
        callbacks: {
          // Show the absolute raw value with thousand separators in the tooltip.
          label(context: {
            datasetIndex: number;
            dataIndex: number;
            raw: unknown;
          }) {
            const datasetIndex = context.datasetIndex;
            const index = context.dataIndex;
            const value = shouldNormalize
              ? (data.datasets[datasetIndex]?.rawData[index] || context.raw)
              : context.raw;
            const formattedValue = Number(value).toLocaleString();
            const label = data.datasets[datasetIndex]?.label || '';
            return label ? `${label}: ${formattedValue}` : formattedValue;
          },
        },
      },
    },
    scales: {
      y: {
        ticks: {
          color: theme.textColors.primary,
          ...(shouldNormalize ? {
            callback(value: number | string) {
              return `${value}%`;
            },
          } : {}),
        },
      },
      x: {
        ticks: {
          color: theme.textColors.primary,
          // With this many labels there's no room to render them legibly anyway, and skipping
          // them entirely avoids chart.js' label-fitting/rotation pass, which is costly at this size.
          display: !isLargeDataset,
        },
      },
    },
  }), [theme, shouldNormalize, mutableMetrics.barValues.length, isLargeDataset, isHugeDataset, data]);

  return (
    <div style={{ maxWidth: '100%', minWidth: 200 }}>
      <OverlayScrollbarComponent
        style={{ maxWidth: '100%' }}
        type={ScrollbarType.Horizontal}
      >
        <div
          css={{
            display: 'block',
            minWidth: 'unset',
            width: data.labels.length * widthPerLabel,
            height: 238,
          }}
        >
          <Chart
            type="bar"
            data={data}
            options={options}
          />
        </div>
      </OverlayScrollbarComponent>
    </div>
  );
};
